'use client';

import { useCallback, useEffect, useRef, useState } from 'react';
import useLoadOnInteraction from '../../hooks/useLoadOnInteraction';

declare global {
  namespace JSX {
    interface IntrinsicElements {
      'wistia-player': {
        'media-id': string;
        aspect?: string;
        className?: string;
        ref?: React.RefObject<HTMLElement | null>;
      };
    }
  }
}

const WISTIA_PLAYER_SCRIPT_ID = 'wistia-player-script';

const POSTER = {
  mobile: '/images/middleware-opsai-demo-thumbnail-338w.webp',
  desktop676: '/images/middleware-opsai-demo-thumbnail-676w.webp',
  desktop1200: '/images/middleware-opsai-demo-thumbnail-1200w.webp',
} as const;

/** 16:9 poster slot — matches generated WebP variants */
const POSTER_WIDTH = 338;
const POSTER_HEIGHT = 190;

type Props = {
  videoId: string;
};

function loadScript(
  id: string,
  src: string,
  options?: { type?: string },
): Promise<void> {
  return new Promise((resolve, reject) => {
    const existing = document.getElementById(id) as HTMLScriptElement | null;
    if (existing) {
      if (existing.dataset.loaded === 'true') {
        resolve();
        return;
      }
      existing.addEventListener('load', () => resolve(), { once: true });
      existing.addEventListener('error', () => reject(new Error(`Failed to load ${src}`)), {
        once: true,
      });
      return;
    }

    const script = document.createElement('script');
    script.id = id;
    script.src = src;
    script.async = true;
    if (options?.type) script.type = options.type;
    script.onload = () => {
      script.dataset.loaded = 'true';
      resolve();
    };
    script.onerror = () => reject(new Error(`Failed to load ${src}`));
    document.body.appendChild(script);
  });
}

function loadWistiaScripts(videoId: string): Promise<void> {
  const embedScriptId = `wistia-embed-script-${videoId}`;
  return loadScript(WISTIA_PLAYER_SCRIPT_ID, 'https://fast.wistia.com/player.js').then(() =>
    loadScript(embedScriptId, `https://fast.wistia.com/embed/${videoId}.js`, {
      type: 'module',
    }),
  );
}

type WistiaPlayerElement = HTMLElement & { play?: () => Promise<void> | void };

async function waitForPlayerRef(
  getRef: () => WistiaPlayerElement | null,
): Promise<WistiaPlayerElement> {
  for (let attempt = 0; attempt < 60; attempt += 1) {
    const player = getRef();
    if (player?.play) return player;
    await new Promise((resolve) => requestAnimationFrame(resolve));
  }
  throw new Error('Wistia player not ready');
}

export default function WistiaHeroPlayer({ videoId }: Props) {
  const playerRef = useRef<HTMLElement | null>(null);
  const [scriptsEnabled, setScriptsEnabled] = useState(false);
  const [playerReady, setPlayerReady] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);

  useLoadOnInteraction(() => {
    setScriptsEnabled(true);
  });

  useEffect(() => {
    if (!scriptsEnabled) return;

    let cancelled = false;

    loadWistiaScripts(videoId)
      .then(() => customElements.whenDefined('wistia-player'))
      .then(() => {
        if (!cancelled) setPlayerReady(true);
      })
      .catch(() => {
        if (!cancelled) setPlayerReady(false);
      });

    return () => {
      cancelled = true;
      setPlayerReady(false);
      setIsPlaying(false);
    };
  }, [scriptsEnabled, videoId]);

  const ensureScriptsAndPlay = useCallback(async () => {
    if (!scriptsEnabled) {
      setScriptsEnabled(true);
    }

    await loadWistiaScripts(videoId);
    await customElements.whenDefined('wistia-player');
    const player = await waitForPlayerRef(
      () => playerRef.current as WistiaPlayerElement | null,
    );

    await player.play();
    setPlayerReady(true);
    setIsPlaying(true);
  }, [scriptsEnabled, videoId]);

  const onPlayClick = () => {
    void ensureScriptsAndPlay();
  };

  return (
    <div className="hero-video">
      {!isPlaying && (!playerReady || !scriptsEnabled) && (
        <picture className="hero-poster-picture">
          <source
            type="image/webp"
            media="(max-width: 768px)"
            srcSet={POSTER.mobile}
          />
          <source
            type="image/webp"
            media="(min-width: 769px)"
            srcSet={`${POSTER.desktop676} 676w, ${POSTER.desktop1200} 1200w`}
            sizes="(min-width: 769px) min(92vw, 1200px)"
          />
          <img
            src={POSTER.mobile}
            alt="Middleware Demo"
            width={POSTER_WIDTH}
            height={POSTER_HEIGHT}
            loading="lazy"
            decoding="async"
            className="hero-poster"
          />
        </picture>
      )}

      {scriptsEnabled && (
        <wistia-player
          ref={playerRef}
          media-id={videoId}
          aspect="1.7777777777777777"
          className={playerReady ? 'is-ready' : 'is-loading'}
        />
      )}

      <div className={`overlay ${isPlaying ? 'hide' : ''}`}>
        <button className="glass-play" onClick={onPlayClick} type="button">
          <span>▶</span>
          <span>Play</span>
        </button>
      </div>

      <style jsx>{`
        .hero-video {
          position: relative;
          width: 100%;
          border-radius: 16px;
          overflow: hidden;
        }

        .hero-video :global(.hero-poster-picture) {
          display: block;
          width: 100%;
          aspect-ratio: 16 / 9;
        }

        .hero-video :global(.hero-poster) {
          width: 100%;
          height: auto;
          display: block;
          border-radius: 10px;
          aspect-ratio: 16 / 9;
          object-fit: contain;
        }

        wistia-player {
          display: block;
          width: 100%;
        }

        wistia-player.is-loading {
          position: absolute;
          width: 0;
          height: 0;
          overflow: hidden;
          opacity: 0;
          pointer-events: none;
        }

        wistia-player::part(big-play-button) {
          display: none;
        }

        .overlay {
          position: absolute;
          inset: 0;
          display: flex;
          align-items: center;
          justify-content: center;
          transition: opacity 0.3s ease;
        }

        .overlay.hide {
          opacity: 0;
          pointer-events: none;
        }

        .glass-play {
          display: flex;
          align-items: center;
          gap: 8px;
          padding: 17px 37px;
          border-radius: 999px;
          border: 1px solid rgba(255, 255, 255, 0.4);
          background: #5b5fc7;
          color: white;
          cursor: pointer;
          backdrop-filter: blur(5px);
        }

        .glass-play span {
          font-size: 23px;
          font-weight: 500;
          line-height: 24px;
          color: white;
        }
      `}</style>
    </div>
  );
}
 